import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { EASE } from "./Reveal";
import Logo from "./Logo";
import Wordmark from "./Wordmark";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    setTimeout(() => {
      navigate(to);
      window.scrollTo(0, 0);
    }, open ? 350 : 0);
  };

  const links = [
    { id: "/chi-siamo", label: t.nav.about },
    { id: "/metodo", label: t.nav.metodo },
    { id: "/works", label: t.nav.portfolio },
    { id: "/showroom", label: t.nav.showroom },
    { id: "/contatti", label: t.nav.contact },
  ];

  return (
    <>
      <motion.header
        data-testid="site-header"
        className={`fixed top-0 left-0 w-full z-[100] transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "bg-ink/85 backdrop-blur-xl border-b border-line/70"
            : "bg-transparent border-b border-transparent"
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.6, ease: EASE }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <button
            data-testid="header-logo-button"
            onClick={() => go("/")}
            className="flex items-center gap-3 group"
            aria-label="Green Gate — home"
          >
            <Logo className="w-8 h-8" />
            <Wordmark className="h-[17px] w-auto text-cream group-hover:text-brass transition-colors duration-300" />
          </button>

          <nav className="hidden xl:flex items-center gap-9">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id.replace(/\//g, "")}`}
                onClick={() => go(l.id)}
                className="relative text-[13px] uppercase tracking-[0.16em] text-stone hover:text-cream transition-colors duration-300 group py-2"
              >
                {l.label}
                <span className="absolute bottom-0 left-0 h-px w-0 bg-brass group-hover:w-full transition-[width] duration-400" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-6">
            <div
              data-testid="lang-toggle"
              className="flex items-center gap-1 text-[13px] tracking-widest"
            >
              {["it", "en"].map((l, i) => (
                <span key={l} className="flex items-center">
                  {i === 1 && <span className="text-stone/50 mx-0.5">/</span>}
                  <button
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`uppercase transition-colors duration-300 ${
                      lang === l ? "text-brass font-semibold" : "text-stone hover:text-cream"
                    }`}
                  >
                    {l}
                  </button>
                </span>
              ))}
            </div>

            <button
              data-testid="header-cta"
              onClick={() => go("/contatti")}
              className="hidden md:inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.16em] text-brass border border-brass/50 rounded-full px-6 py-2.5 hover:bg-brass hover:text-ink transition-colors duration-400"
            >
              {t.nav.cta}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              className="xl:hidden text-cream p-2"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[150] bg-ink/98 backdrop-blur-xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
          >
            <div className="max-w-[1400px] w-full mx-auto px-6 h-[72px] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Logo className="w-8 h-8" />
                <Wordmark className="h-5 w-auto text-cream" />
              </div>
              <button
                data-testid="mobile-menu-close"
                className="text-cream p-2"
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
              >
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[...links, { id: "/contatti", label: t.collab.overline }].map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id.replace(/\//g, "")}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-4xl text-cream hover:text-brass transition-colors duration-300 py-3 border-b border-line/50 flex items-center justify-between"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  {l.label}
                  <ArrowUpRight size={22} className="text-brass" strokeWidth={1.25} />
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10 text-stone text-sm">{t.contact.info.phone}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
